// ============================================================
// VitalsSection - how hurt, how many, and what stands between.
//
// Two kinds of number sit side by side here and are locked apart: the
// maximum is the SHEET's (what the character is), the current value and
// the cover are live STATE (what has happened to them).
// ============================================================
import type { Character } from '../../engine/character/types';
import { COVER_LABELS, COVER_LEVELS, LOC_NAMES } from '../../engine/constants';
import { isCrowd, crowdHitsText } from '../../engine/bodyShape';
import { withPath } from '../../engine/targetEdits';
import type { SheetEditor as SheetEditorApi } from '../../hooks/useSheetEditor';
import type { Target } from '../../types';
import ReadoutNumber from './ReadoutNumber';
import styles from './Sheet.module.css';

export interface SectionProps {
  character: Character;
  target: Target;
  /**
   * Lay a partial over the sheet. `release` names the pins the edit lets
   * go first, and the rule for a control is simple: one that SHOWS the
   * effective value (`target.*`) releases the pin at that path, so it is
   * obeyed even when the sheet already says the same; one that shows the
   * sheet's own value (`character.*`) passes nothing.
   */
  onChange: SheetEditorApi['editSheet'];
  /** Pin a flat value over the sheet: what only the table can say. */
  onPin: SheetEditorApi['setPin'];
  /** The host panel's writer for the LIVE STATE: current wounds, cover, worn armour, facing. */
  onTargetChange: (next: Target) => void;
  sheetLocked: boolean;
  stateLocked: boolean;
  /** The combatant's live state (the stance bench's preview judges a condition about oneself on it). */
  combatantState?: import('../../engine/roster/types').CombatState | null;
  /** The sheet is open in play, not in the library: the stance bench offers "Back to the table". */
  atTable?: boolean;
}

/** A covered location's name. */
const locationWords = (id: string) => LOC_NAMES[id] ?? id;

/** What "health" means for this body: a crowd counts magnitude, anyone else wounds. */
function healthOf(character: Character, target: Target) {
  if (isCrowd(target)) {
    return {
      noun: 'Magnitude',
      current: target.horde?.magnitude ?? 0,
      max: character.horde.maxMagnitude || 30,
      path: 'horde.magnitude',
      // The sheet's own magnitude is what FRESH means, so it follows the maximum (as wounds do, below).
      maxPatch: (value: number): Partial<Character> => ({
        horde: { ...character.horde, magnitude: value, maxMagnitude: value },
      }),
      // A horde attacking scores +1 hit per 10 magnitude on each attack (ruling 2026-10-09).
      note: (current: number) => crowdHitsText(current, true),
      negative: false,
    };
  }
  return {
    noun: 'Wounds',
    current: target.wounds.current,
    max: character.wounds.max,
    path: 'wounds.current',
    // The sheet's own `current` is what FRESH means (`emptyCombatState` reads it first), so it follows
    // the maximum. Where somebody stands is the live state's, or the library entry's standing block.
    maxPatch: (value: number): Partial<Character> => ({ wounds: { current: value, max: value } }),
    note: null,
    negative: true,
  };
}

/** Current over maximum: wounds, or a crowd's magnitude. */
function Health({ character, target, onChange, onTargetChange, sheetLocked, stateLocked }: SectionProps) {
  const health = healthOf(character, target);
  return (
    <div className={styles.healthRow}>
      <ReadoutNumber
        label={`${health.noun} Cur`}
        size="large"
        // Wounds go negative (critical severity); magnitude does not.
        min={health.negative ? undefined : 0}
        allowNegative={health.negative}
        value={health.current}
        disabled={stateLocked}
        onChange={(value) => onTargetChange(withPath(target, health.path, value))}
      />
      <span className={styles.healthOf}>of</span>
      <ReadoutNumber
        label={`${health.noun} Max`}
        min={1}
        value={health.max}
        disabled={sheetLocked}
        onChange={(value) => onChange(health.maxPatch(value))}
      />
      {health.note && <span className={styles.sectionNote}>{health.note(health.current)}</span>}
    </div>
  );
}

/**
 * Cover set BY HAND, which marks it manual. That mark is what makes it
 * outrank the map: the battlefield projects a live answer per attacker,
 * and a combatant carrying manual cover is exempt until it is cleared.
 */
function Cover({ target, onTargetChange, stateLocked }: SectionProps) {
  const level = target.cover?.level || 'none';
  const setCover = (fields: Partial<Target['cover']>) =>
    onTargetChange({ ...target, cover: { ...target.cover, ...fields, source: 'manual' } } as Target);
  return (
    <>
      <label className={styles.readout}>
        <span className={styles.readoutLabel}>Cover</span>
        <select
          className={styles.select}
          aria-label="Cover"
          value={level}
          disabled={stateLocked}
          onChange={(e) => setCover({ level: e.target.value as Target['cover']['level'] })}
        >
          {Object.entries(COVER_LABELS).map(([key, text]) => (
            <option key={key} value={key}>
              {text}
            </option>
          ))}
        </select>
      </label>
      {level !== 'none' && (
        <ReadoutNumber
          label="Cover AP"
          min={0}
          value={target.cover?.ap || 0}
          disabled={stateLocked}
          onChange={(ap) => setCover({ ap })}
        />
      )}
    </>
  );
}

export default function VitalsSection(props: SectionProps) {
  const { character, target, onChange, sheetLocked } = props;
  const covered = COVER_LEVELS[target.cover?.level]?.map(locationWords).join(', ');
  return (
    <>
      <Health {...props} />
      <div className={styles.factRow}>
        <ReadoutNumber
          label="Psy Rating"
          min={0}
          value={character.psyRating ?? 0}
          disabled={sheetLocked}
          testId="psy-rating-input"
          onChange={(psyRating) => onChange({ psyRating })}
        />
        <ReadoutNumber
          label="Fear"
          title="Fear (X): enemies test Willpower to face it. 0 for none."
          min={0}
          max={4}
          value={character.fearRating ?? 0}
          disabled={sheetLocked}
          onChange={(fearRating) => onChange({ fearRating })}
        />
        <Cover {...props} />
      </div>
      {covered && <div className={styles.sectionNote}>Covered: {covered}. Spray ignores cover.</div>}
    </>
  );
}
